import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Heart, Globe2, Shield } from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";
import DemoModal from "../components/marketing/DemoModal";

const VALUES = [
  { icon: Target,  title: "Sellers first.",     body: "Every feature begins with a real D2C founder's pain point — not an AI hype cycle." },
  { icon: Heart,   title: "Honest analytics.",   body: "We surface the bad SKUs too. No vanity dashboards. No agency-style sugar-coating." },
  { icon: Globe2,  title: "Built for India.",    body: "Native Myntra, Nykaa, Ajio, Amazon, Flipkart and quick-commerce flows — not Western retrofits." },
  { icon: Shield,  title: "Your data, yours.",   body: "Single-tenant data isolation. No cross-brand training. Export everything on demand." },
];

const TIMELINE = [
  { year: "Jan 2025", what: "EcomCatalog AI founded. First seller onboarded in week one." },
  { year: "Q2 2025",  what: "AI Auto-Listing launched — 90-second flow live on Myntra." },
  { year: "Q3 2025",  what: "Multi-marketplace exports added: Nykaa, Ajio, Amazon, Flipkart." },
  { year: "Q4 2025",  what: "Competitor Price Sniper + Growth AI dashboard shipped." },
  { year: "Feb 2026", what: "Quick-commerce playbook expanded: Blinkit, Instamart, Zepto." },
];

export default function About() {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "About — EcomCatalog AI · Built for Indian D2C Sellers";
  }, []);

  return (
    <MarketingShell onBookDemo={() => setDemoOpen(true)}>
      <section className="pt-16 sm:pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            About
          </div>
          <h1 className="font-mkt-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter mt-4 leading-[1.02] max-w-4xl">
            We're building the <span className="mkt-gradient-text-vivid">growth stack</span> that Indian D2C deserves.
          </h1>
          <p className="text-lg text-slate-400 mt-7 max-w-3xl leading-relaxed">
            EcomCatalog AI was started in 2025 after our founder spent two years inside D2C
            ops watching brilliant brands lose entire weekends to spreadsheet listings, agency
            invoices and dashboards that hid more than they revealed. We're rebuilding the
            entire seller stack — listings, creative, analytics, growth — around AI that
            actually understands Indian ecommerce.
          </p>
        </div>
      </section>

      {/* Mission card */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">Mission</div>
                <h2 className="font-mkt-display text-2xl font-semibold tracking-tight mt-2">
                  Let every brand operate like it has a 20-person growth team.
                </h2>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-6">
                <div>
                  <div className="font-mkt-display text-4xl font-semibold mkt-gradient-text">187+</div>
                  <div className="text-xs text-slate-400 mt-1">D2C brands in beta</div>
                </div>
                <div>
                  <div className="font-mkt-display text-4xl font-semibold mkt-gradient-text">2.4L+</div>
                  <div className="text-xs text-slate-400 mt-1">AI listings shipped</div>
                </div>
                <div>
                  <div className="font-mkt-display text-4xl font-semibold mkt-gradient-text">7</div>
                  <div className="text-xs text-slate-400 mt-1">Marketplaces integrated</div>
                </div>
                <div>
                  <div className="font-mkt-display text-4xl font-semibold mkt-gradient-text">90s</div>
                  <div className="text-xs text-slate-400 mt-1">Avg listing time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">Values</div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              What we believe.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="mkt-glass mkt-hover-lift p-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 grid place-items-center mb-4">
                  <v.icon size={18} className="text-indigo-300" />
                </div>
                <div className="font-mkt-display text-lg font-semibold tracking-tight">{v.title}</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            Journey
          </div>
          <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
            How we got here.
          </h2>
          <div className="mt-10 relative">
            <div className="absolute left-3 top-1 bottom-1 w-px bg-gradient-to-b from-indigo-500/50 via-purple-500/30 to-transparent" />
            {TIMELINE.map((t, i) => (
              <div key={i} className="relative pl-10 pb-9 last:pb-0">
                <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 border-2 border-[#030712] shadow-[0_0_0_4px_rgba(79,70,229,0.15)]" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">{t.year}</div>
                <div className="font-mkt-display text-lg font-medium text-white mt-1">{t.what}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-purple-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <h3 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                Build with us.
              </h3>
              <p className="text-slate-400 mt-4 max-w-xl mx-auto">
                We're hiring for engineering, AI/ML and growth. Write to{" "}
                <a href="mailto:business@ecomcatalog.ai" className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline">
                  business@ecomcatalog.ai
                </a>.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contact" className="mkt-btn-gradient">
                  Get in touch <ArrowUpRight size={14} />
                </Link>
                <Link to="/features" className="mkt-btn-ghost">
                  See product
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </MarketingShell>
  );
}
